import {
  createQueryablePromiseFromPromise,
  MovieClip,
} from '@play-co/timestep-core/ui';
import { trackPetSpawn } from 'src/lib/analytics/events';
import { openPopupPromise } from 'src/lib/popups/popupOpenClose';
import sounds from 'src/lib/sounds';
import { getCurrentScene } from 'src/lib/stateUtils';
import {
  delay,
  getSceneManager,
  waitForAssets,
  withLoading,
} from 'src/lib/utils';
import {
  accessionAssets,
  petCrateAssets,
  petSpeciesAssets,
} from 'src/loadingGroups';
import * as Pet from 'src/replicant/pet';
import { setPetCrateOpening } from 'src/state/ui';
import StateObserver from 'src/StateObserver';
import { careActions } from '../care/care.redux';
import { levelUpSequence } from '../level/level.sequence';
import { PetController } from '../pet/PetController';
import { appendToConversationData } from '../conversation/conversation.utils';
import { renamePetSequence } from '../pet/pet.sequence';
import { Feature } from 'src/replicant/analytics';
import { track } from 'src/lib/analytics/utils';
import { PET_CRATE_HEIGHT_RATIO } from '../position/position.config';

/**
 * Death sequence
 */
export async function ascensionSequence(pet: PetController) {
  // Load ascension assets while waiting for conversation to finish
  const assetsLoaded = createQueryablePromiseFromPromise(
    Promise.resolve(accessionAssets.isLoaded() || accessionAssets.load()),
  );
  await pet.conversation.waitForFinish();

  // Play ascension sound
  if (!assetsLoaded.isReady()) {
    await withLoading(() => assetsLoaded, {
      feature: Feature.ASCENSION._,
      subFeature: Feature.ASCENSION.ASSETS,
    });
  }
  sounds.playSound('ascension');

  // Move to center of the screen and play ascension animation
  await pet.moveTo({
    scene: getSceneManager().baseSceneId,
    location: 'c',
  });
  pet.position.setWandering(false);
  pet.appearance.setAction('ascension');

  // Wait a second and then show white screen animation
  await delay(1000);
  const hideWhiteScreenAnimation =
    await getSceneManager().showWhiteScreenEffect(2000);

  // Pause a bit here
  await delay(1000);

  // Deliver new pet
  const reborn = rebornSequence(pet);

  // Hide white screen animation
  await hideWhiteScreenAnimation();

  // Block on reborn sequence
  await reborn;
}

/**
 * Deliver new pet after death
 */
async function rebornSequence(pet: PetController) {
  // Freeze pet
  pet.appearance.setAction(undefined);
  pet.appearance.setSpecies(undefined);

  // Hide pet movie clip
  pet.hide();

  // Load pet assets
  await waitForAssets(petCrateAssets, {
    feature: Feature.ASCENSION._,
    subFeature: Feature.ASCENSION.CRATE_ASSETS,
  });

  // Stop all pet animations
  const { height } = StateObserver.getState().ui.screenSize;
  pet.position.setWandering(false);

  // Hide pet information
  StateObserver.dispatch(setPetCrateOpening(true));

  // Create crate animation
  const sceneManager = getSceneManager();
  const scene = getCurrentScene();

  if (!sceneManager.isValidScene(scene)) {
    throw new Error('Trying to spawn a pet in an invalid scene');
  }

  const superview = sceneManager.getScene(scene)?.getView();

  if (!superview) {
    throw new Error('Trying to spawn a pet in an invalid scene');
  }

  const petCrate = new MovieClip({
    superview,
    x: 365,
    y: height * PET_CRATE_HEIGHT_RATIO,
    centerOnOrigin: true,
    zIndex: 1,
    url: 'assets/tutorial/crate',
    defaultAnimation: 'pet_crate_idle',
    offsetX: 170,
    offsetY: 124,
  });

  // Show dialog
  await delay(500);
  await openPopupPromise('popupAcceptToS', { showTos: false });

  // Remember previous pet evolution
  const previousEvolution = Pet.getters.getCurrentEvolution(
    StateObserver.getState().user,
  );

  // Analytics. Should be called before new pet is spawned.
  track('PetDie', {});

  // Decline all duo invitations.
  await StateObserver.invoke.cancelAllDuoInvitations();

  // Create new pet
  const oldState = StateObserver.getState().user;
  await StateObserver.invoke.rebornPet();

  // Load assets for species
  const { user } = StateObserver.getState();
  const evolution = Pet.getters.getCurrentEvolution(user);
  if (!evolution) {
    throw new Error('Trying to spawn a pet without an evolution');
  }

  const { species } = evolution;
  await waitForAssets(petSpeciesAssets[species], {
    feature: Feature.ASCENSION._,
    subFeature: Feature.ASCENSION.PET_ASSETS,
  });
  appendToConversationData(species);

  // Play open crate animation
  petCrate.play('pet_crate_open', () => {
    petCrate.removeFromSuperview();
  });

  // Show pet during crate opening animation
  await delay(1250);

  // Set species skin
  pet.appearance.setSpecies(species);

  // Show pet movie clip
  pet.show();

  // Set pet positions
  void pet.moveTo({
    scene: getSceneManager().baseSceneId,
    location: 'c',
  });

  // Show greetings messages
  await pet.startConversation('initial_greetings');

  // Update name
  await renamePetSequence({ origin: undefined });

  // Show level up popup if needed.
  await levelUpSequence(undefined);

  // Show new pet information
  StateObserver.dispatch(setPetCrateOpening(false));

  // Allow walk
  pet.position.setWandering(true);
  StateObserver.dispatch(careActions.setCareAction(null));

  // Schedule activity
  await StateObserver.invoke.schedulePetActivity();

  // Spawn analytics
  trackPetSpawn({
    feature: 'death',
    subFeature: `death_${previousEvolution?.stage ?? 'baby'}`,
  });
}
